import { SaveOperation } from '@infuse/api/common';
import { UpdateOrganisation } from '@infuse/api/iam/organisation';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

export const useGenericUpdateOrganisation = ({
  apiFn,
  forcedParentId
}: {
  apiFn: SaveOperation<UpdateOrganisation>;
  forcedParentId?: string;
}) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<unknown, unknown, { parentId?: string; id: string; organisation: UpdateOrganisation }>({
    mutationFn: async ({ parentId, id, organisation }) => {
      if (forcedParentId) {
        parentId = forcedParentId;
      }

      try {
        await apiFn(parentId ? { parentId: parentId, id: id } : { id: id }, organisation);
        notify({
          title: t('iam.organisations.header.update_organisation'),
          message: t('iam.organisations.notify.update_organisation_success')
        });
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.organisations.header.update_organisation'),
          message: getAxiosErrorMessage(e, t('iam.organisations.notify.update_organisation_failed'))
        });
        throw e;
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['organisations'] });
    }
  });

  return {
    ...mutation
  };
};
